import { Activity } from 'lucide-react';
import { Fragment } from 'react';
import { z } from 'zod';

import { defineStandalone } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { EventLine } from '@apps/web/features/phoebe_chat_v3/ui/primitives/event_line';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';

const PAST_COPY = 'Finished checking this';

const watchPayloadSchema = z
    .object({
        type: z.literal('watch').optional(),
        watch_id: z.string(),
        reason: z.string(),
        kind: z.enum(['delta', 'expired', 'suspended', 'completed']),
    })
    .strip();

export const watchDefinitions = {
    watch: defineStandalone('watch', {
        disposition: 'render',
        schema: watchPayloadSchema,
        malformed: {
            kind: 'placeholder',
            customerCopy: PAST_COPY,
        },
        evidence: null,
        // Every watch event is a receipt of work already done, so it stays
        // as its own row. Live watches reach the stack from run state.
        group: null,
        render(payload) {
            const reason = <ResolvedReferenceText text={payload.reason} />;
            // A fire's news is the subject itself, so it leads; an ending's
            // news is that the watch stopped, so the verb leads.
            return (
                <EventLine glyph={<Activity />}>
                    {payload.kind === 'delta' ? (
                        <Fragment>{reason} · changed</Fragment>
                    ) : (
                        <Fragment>
                            {endedLabel(payload.kind)} · {reason}
                        </Fragment>
                    )}
                </EventLine>
            );
        },
    }),
} as const;

export const watchContractFixtures = {
    valid: {
        type: 'watch',
        watch_id: 'watch-1',
        reason: 'Internal monitoring reason',
        kind: 'delta',
    },
    malformed: {
        type: 'watch',
        watch_id: 7,
        reason: 'Internal monitoring reason',
        kind: 'delta',
    },
} as const;

function endedLabel(kind: 'expired' | 'suspended' | 'completed') {
    switch (kind) {
        case 'completed':
            return 'Finished watching';
        // A suspended watch is never evaluated again, so it reads as
        // stopped rather than paused.
        case 'expired':
        case 'suspended':
            return 'Stopped watching';
        default: {
            const exhaustiveKind: never = kind;
            return exhaustiveKind;
        }
    }
}
